import { Link } from "react-router-dom";
import { StatusChip } from "./RegulationRadar";
import { PolicyHead } from "./PolicyIntelligence";
import { sectorTrack } from "./SectorHero";

/* POLICY IMPACT — IMPACT PATHWAYS (§19-21). Restrained institutional treatment:
   each tracked development connects to the parts of the onchain economy its own
   structured record demonstrably affects. Every chip carries matched evidence.
   Deliberately NOT an energy-network visual — Policy speaks a different language. */
const ImpactRow = ({ item }: { item: any }) => {
  const reg = item.regulation;
  return (
    <div data-testid={`impact-row-${reg.slug}`}
      className="grid lg:grid-cols-[300px_56px_minmax(0,1fr)] gap-3 lg:gap-0 items-center py-4 border-b border-white/[0.06] last:border-0">
      <Link to={`/regulation/${reg.slug}`}
        onClick={() => sectorTrack("regulation_click", reg.slug, "policy")}
        className="group min-w-0">
        <span className="flex items-center gap-2">
          <StatusChip status={reg.status} />
          <span className="font-mono text-[8.5px] font-bold tracking-wide text-gray-500">
            {(reg.codes || []).join("·")}
          </span>
        </span>
        <span className="block text-white font-semibold text-[13.5px] leading-snug mt-1.5 group-hover:text-[#A78BFA] transition-colors">
          {reg.title}
        </span>
      </Link>
      <span aria-hidden className="hidden lg:block h-px w-full self-center"
        style={{ background: "linear-gradient(to right, rgba(167,139,250,0.45), rgba(0,229,255,0.35))" }} />
      <div className="min-w-0">
        <div className="flex flex-wrap gap-2">
          {item.impacts.map((im: any) => (
            <span key={im.family} title={`Evidence: ${im.evidence.join(" · ")}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#00E5FF]/25 bg-[#00E5FF]/[0.05] px-2.5 py-1 font-mono text-[9.5px] font-bold uppercase tracking-wide text-gray-200">
              <span aria-hidden className="h-1 w-1 rounded-full bg-[#00E5FF]/80" />
              {im.family}
            </span>
          ))}
        </div>
        <p className="font-mono text-[8px] uppercase tracking-wide text-gray-600 mt-2 leading-relaxed">
          Evidence: {item.impacts.map((im: any) => `${im.family} — ${im.evidence[0]}`).join(" · ")}
        </p>
      </div>
    </div>
  );
};

export const PolicyImpact = ({ items, note }: { items: any[]; note?: string }) => {
  if (!items?.length) return null;
  return (
    <section data-testid="policy-impact" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-12">
      <PolicyHead title="Policy Impact" chip="Impact Pathways" testid="policy-impact-head"
        desc="Which parts of the onchain economy each tracked development materially affects — deterministic and evidence-backed." />
      <div className="relative overflow-hidden rounded-xl border border-white/[0.08] bg-[#0a0b10] px-4 sm:px-6 py-2">
        <div aria-hidden className="absolute -top-24 right-1/4 h-48 w-96 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(0,229,255,0.05), transparent)" }} />
        {items.map((it) => <ImpactRow key={it.regulation.slug} item={it} />)}
      </div>
      {note && (
        <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-gray-600 mt-3 leading-relaxed">{note}</p>
      )}
    </section>
  );
};
